// Frontend anonymization utilities
// Mirrors backend anonymizer for consistent masking in UI

/**
 * Mask email address for privacy while keeping domain visible
 * Format: f***n@domain.com (first char, stars, last char)
 * @param {string} email - The email to mask
 * @returns {string} Masked email
 */
export function maskEmail(email) {
    if (!email || typeof email !== 'string') return email;
    const atIndex = email.indexOf('@');
    if (atIndex === -1) return email;

    const localPart = email.substring(0, atIndex);
    const domain = email.substring(atIndex);

    if (localPart.length <= 2) {
        return email; // Too short to mask meaningfully
    }

    const firstChar = localPart[0];
    const lastChar = localPart[localPart.length - 1];
    const middleStars = '*'.repeat(localPart.length - 2);

    return `${firstChar}${middleStars}${lastChar}${domain}`;
}

/**
 * Mask phone number for privacy
 * Format: +*** **** **** (last 4 digits visible)
 * @param {string} phone - The phone number to mask
 * @returns {string} Masked phone number
 */
export function maskPhone(phone) {
    if (!phone || typeof phone !== 'string') return phone;

    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');

    if (digits.length < 4) {
        return phone; // Too short to mask
    }

    // Preserve last 4 digits and mask the rest while keeping formatting
    const last4 = digits.slice(-4);
    const countryCode = digits.length > 10 ? digits.slice(0, digits.length - 10) : '';
    const areaCode = digits.length > 7 ? digits.slice(countryCode.length, digits.length - 7) : '';

    // Build masked phone string
    let masked = '';
    if (countryCode) masked += `+${countryCode} `;
    if (areaCode) masked += `***-`;
    masked += `${last4}`; // Append last 4 digits

    return masked;
}

/**
 * Mask username for privacy
 * Format: f***r (first and last char visible)
 * @param {string} username - The username to mask
 * @returns {string} Masked username
 */
export function maskUsername(username) {
    if (!username || typeof username !== 'string') return username;

    if (username.length <= 2) {
        return username; // Too short to mask
    }

    const firstChar = username[0];
    const lastChar = username[username.length - 1];
    const middleStars = '*'.repeat(username.length - 2);

    return `${firstChar}${middleStars}${lastChar}`;
}

/**
 * Mask full name for privacy
 * Format: F***y (first initial and last initial visible)
 * @param {string} name - The full name to mask
 * @returns {string} Masked name
 */
export function maskFullName(name) {
    if (!name || typeof name !== 'string') return name;

    const words = name.trim().split(' ');
    if (words.length === 0) return name;

    if (words.length === 1) {
        // Single word name
        const word = words[0];
        if (word.length <= 2) return word;
        return `${word[0]}***${word[word.length - 1]}`;
    }

    // Multiple words - show first initial and last initial
    const firstInitial = words[0][0];
    const lastInitial = words[words.length - 1][0];
    return `${firstInitial}***${lastInitial}`;
}